import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { searchResultsSelector, selectParticipantsFromSearchResults } from 'models/searchResults';
import { UserSessionContext } from 'components/UserSession';
import { TabsContext } from 'components/TabsContext';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import AllSearchResultContainer from 'components/search/AllSearchResultContainer';
import SearchResultContainer from 'components/search/SearchResultContainer';
import ParticipantSearchResultContainer from 'components/search/ParticipantSearchResultContainer';
import EmptyStatePlaceholder from 'components/search/EmptyStatePlaceholder';

const RootContainer = styled.div`
  flex: 1 0 auto;
  width: 100%;
  padding: 0 10px;
  margin-bottom: 36px;
`;

const StyledEmptyStatePlaceholder = styled(EmptyStatePlaceholder)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  height: auto;
`;

const SearchResultTabContentContainer = ({
  showResult,
  fetchMore,
  scrollableTarget,
  viewableTabsList,
}) => {
  const {
    initialLoading,
  } = useSelector((s) => s.searchResults);
  const {
    exhibitors,
    webinars,
    products,
    speakers,
  } = useSelector(searchResultsSelector);
  const { features } = useSelector((s) => s.eventConfigs);
  const { activeTabKey: activeTab } = useContext(TabsContext);
  const { canSee, userSession: { role } } = useContext(UserSessionContext);
  const participants = useSelector(selectParticipantsFromSearchResults(role));

  const resultsPresent = (exhibitors && exhibitors.length > 0)
    || (webinars && webinars.length > 0)
    || (participants && participants.length > 0)
    || (products && products.length > 0)
    || (speakers && speakers.length > 0);

  if (showResult) {
    if (initialLoading) {
      return (
        <RootContainer>
          <CircularLoadingIndicator size={32} />
        </RootContainer>
      );
    }
    if (!resultsPresent) {
      return (
        <RootContainer>
          <StyledEmptyStatePlaceholder />
        </RootContainer>
      );
    }
  }

  return (
    <RootContainer>
      {
        activeTab === 0 && (
          <AllSearchResultContainer
            showResult={showResult}
            exhibitors={exhibitors}
            webinars={webinars}
            products={products}
            participants={participants}
            speakers={speakers}
            fetchMore={fetchMore}
            scrollableTarget={scrollableTarget}
          />
        )
      }
      {
        activeTab === 1 && showResult && (
          <SearchResultContainer
            type="exhibitor"
            results={exhibitors}
          />
        )
      }
      {
        canSee('webinar') && viewableTabsList[activeTab] === 'webinar' && showResult && (
          <SearchResultContainer
            type="webinar"
            results={webinars}
          />
        )
      }
      {
        canSee('participant') && viewableTabsList[activeTab] === 'participants' && showResult && (
          <ParticipantSearchResultContainer
            participants={participants}
            fetchMore={fetchMore}
            scrollableTarget={scrollableTarget}
          />
        )
      }
      {
        features.includes('productPage') && viewableTabsList[activeTab] === 'products' && showResult && (
          <SearchResultContainer
            type="products"
            results={products}
          />
        )
      }
      {
        canSee('webinar') && viewableTabsList[activeTab] === 'speakers' && showResult && (
          <SearchResultContainer
            type="speakers"
            results={speakers}
          />
        )
      }
    </RootContainer>
  );
};

SearchResultTabContentContainer.propTypes = {
  showResult: PropTypes.bool.isRequired,
  fetchMore: PropTypes.func.isRequired,
  scrollableTarget: PropTypes.instanceOf(Element),
  viewableTabsList: PropTypes.arrayOf(PropTypes.string).isRequired,
};

SearchResultTabContentContainer.defaultProps = {
  scrollableTarget: document.body,
};

export default SearchResultTabContentContainer;
